import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Browser autosave for a form's state.
 *
 * Persists a debounced snapshot of `formData` to localStorage so a customer can
 * reload or come back and resume a partially completed form. Client-only: the
 * data lives in the customer's own browser, is cleared on submit, and expires
 * after `ttlMs`. All storage access is wrapped in try/catch so private-mode or
 * disabled storage never breaks the form.
 *
 * The hook does NOT apply the restored draft itself — it returns `restoredDraft`
 * (read once on mount) and lets the caller decide how to merge it, so the
 * mount-time restore order (draft -> URL params -> API prefill) stays in the
 * component that owns that logic.
 *
 * @param {object}   formData          the current form state to persist
 * @param {object}   options
 * @param {string}   options.storageKey localStorage key
 * @param {boolean} [options.enabled]   set false to disable persistence entirely
 * @param {number}  [options.ttlMs]     max age of a restorable draft (default 7 days)
 * @param {number}  [options.debounceMs] save debounce (default 600ms)
 * @param {(formData:object)=>boolean} [options.shouldPersist] guard: only save
 *        when this returns true (skip all-empty/defaults-only snapshots)
 */
export function useFormDraft(
  formData,
  {
    storageKey,
    enabled = true,
    ttlMs = 7 * 24 * 60 * 60 * 1000,
    debounceMs = 600,
    shouldPersist,
  } = {}
) {
  // Read any existing draft exactly once, before the first render's effects run.
  const [restored] = useState(() => {
    if (!enabled || !storageKey) return { data: null, savedAt: null };
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return { data: null, savedAt: null };
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object' || !parsed.data) {
        return { data: null, savedAt: null };
      }
      if (parsed.savedAt && Date.now() - parsed.savedAt > ttlMs) {
        window.localStorage.removeItem(storageKey);
        return { data: null, savedAt: null };
      }
      return { data: parsed.data, savedAt: parsed.savedAt || null };
    } catch {
      return { data: null, savedAt: null };
    }
  });

  const timerRef = useRef(null);

  const clearDraft = useCallback(() => {
    // Cancel any pending debounced write so a scheduled save can't resurrect
    // the draft after we remove it (e.g. clear fired right after a keystroke).
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!storageKey) return;
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      /* ignore storage errors */
    }
  }, [storageKey]);

  useEffect(() => {
    if (!enabled || !storageKey) return undefined;
    if (shouldPersist && !shouldPersist(formData)) return undefined;

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({ savedAt: Date.now(), data: formData })
        );
      } catch {
        /* ignore quota / disabled storage */
      }
    }, debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [formData, enabled, storageKey, debounceMs, shouldPersist]);

  return {
    restoredDraft: restored.data,
    restoredAt: restored.savedAt,
    hasRestoredDraft: !!restored.data,
    clearDraft,
  };
}

export default useFormDraft;
